import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { useWalletStore, type WalletTransaction } from '../stores/walletStore';
import { useCoinRewardStore, type CoinGrant } from '../stores/coinRewardStore';

/**
 * How fresh a row must be to speak for itself on the FIRST read of a session.
 *
 * ⚠️⚠️ Without this, signing in would announce the whole ledger: on the first
 * read every row is new to us, so a user with a year of grants would watch a
 * year of coins fly across the screen. Skipping the first read wholesale is not
 * the answer either — a brand-new account's welcome bundle is written DURING
 * that very read, and it is the one announcement that matters most.
 */
export const FIRST_READ_WINDOW_MS = 10_000;

const toGrants = (rows: WalletTransaction[]): CoinGrant[] =>
  // Rows arrive newest first; announced oldest first, so two grants that landed
  // together are told in the order they happened.
  [...rows].reverse().map((t) => ({
    id: t.id,
    kind: t.kind,
    amountCp: t.amount_cp,
    note: t.note ?? '',
    createdAt: t.created_at,
  }));

/**
 * Which of these ledger rows deserve a card, and which should be swallowed.
 *
 * Pure, and exported for its own sake: the whole rule lives here rather than
 * inside an effect, so it can be tested without a browser.
 */
export function creditsToAnnounce(
  transactions: WalletTransaction[],
  opts: { firstRead: boolean; now: number },
): { announce: CoinGrant[]; suppress: string[] } {
  const credits = transactions.filter((t) => t.amount_cp > 0);
  if (!opts.firstRead) return { announce: toGrants(credits), suppress: [] };

  // ⚠️ An unparseable timestamp yields NaN, and `NaN <= window` is false — so a
  // malformed row goes quiet rather than being announced. Silence is the safe
  // direction here; a wrongly replayed grant is not.
  const fresh = credits.filter((t) => opts.now - Date.parse(t.created_at) <= FIRST_READ_WINDOW_MS);
  const freshIds = new Set(fresh.map((t) => t.id));
  return {
    announce: toGrants(fresh),
    suppress: transactions.filter((t) => !freshIds.has(t.id)).map((t) => t.id),
  };
}

/**
 * Turns coin arriving into something the user is told about.
 *
 * Two halves. The PULL half watches the wallet's transaction list and queues
 * any credit it has not seen — that covers every grant claimed as a side effect
 * of reading the balance (the welcome bundle, the monthly allowance) and
 * everything a refresh happens to bring back.
 *
 * ⭐ The PUSH half is why a marketplace SALE works at all. `buy_marketplace_item`
 * credits the seller, but only the buyer's app refreshes — the seller was told
 * nothing and found the money on some later visit. A realtime ear on their own
 * ledger closes that: the insert arrives, the wallet refreshes, and the pull
 * half above does the rest, so there is exactly one path from row to card.
 *
 * ⚠️ Debits are ignored on both sides. Money leaving is not good news.
 */
export function useCoinGrants(): void {
  const transactions = useWalletStore((s) => s.transactions);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    void (async () => {
      const { user } = await syncManager.safeAuthCheck();
      if (cancelled || !user) return;
      setUserId(user.id);

      // ⚠️ The `filter` is not decoration. `wallet_transactions` carries TWO
      // permissive SELECT policies — read-own AND is_admin() — and Postgres ORs
      // them, so an unfiltered subscription would wake an admin for every user
      // in the system.
      channel = supabase
        .channel(`wallet-credits-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'wallet_transactions',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const row = payload.new as { amount_cp?: number } | null;
            if (!row || Number(row.amount_cp ?? 0) <= 0) return;
            // Refresh rather than announce from the payload: the balance has to
            // move too, and one path from row to card is worth a round trip on
            // something this rare.
            void useWalletStore.getState().refresh();
          },
        )
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    // ⚠️ An EMPTY list is not "no history", it is "no answer yet" — and on a
    // brand-new account it is a race we lose on purpose. `refresh()` fires
    // `get_my_wallet()` (which writes the welcome bundle) and the history query
    // in PARALLEL, so the first list can come back before the grant commits.
    // Seeding on it would mark the welcome bundle as old history the moment it
    // finally appeared. Wait for a list with something in it.
    if (!userId || transactions.length === 0) return;

    const store = useCoinRewardStore.getState();
    const firstRead = store.seededFor !== userId;
    const { announce, suppress } = creditsToAnnounce(transactions, { firstRead, now: Date.now() });
    if (firstRead) store.seed(userId, suppress);
    store.enqueue(announce);
  }, [transactions, userId]);
}
